import React, { useState, useEffect } from 'react'
import { 
  Settings as SettingsIcon, 
  Key, 
  Bell, 
  Bot, 
  Shield,
  ExternalLink,
  Save,
  AlertCircle,
  CheckCircle,
  Activity,
  RefreshCw
} from 'lucide-react'
import { settingsAPI, authAPI } from '../services/api'
import { Radio } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const Settings = () => {
  const { user, connectKite } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  // AngelOne / streaming settings
  const [aoSettings, setAoSettings] = useState({
    angelOneApiKey: '', angelOneClientCode: '',
    angelOneMpin: '', angelOneTotpKey: '',
    streamingProvider: 'kite',
  })
  const [aoSaving, setAoSaving] = useState(false)
  const [settings, setSettings] = useState({
    kiteApiKey: '',
    agentMode: false,
    notifications: {
      email: true,
      telegram: false,
      web: true
    },
    trading: {
      riskPercentage: 2,
      maxPositions: 5,
      autoExecute: false
    },
    telegramChatId: ''
  })
  const [logs, setLogs] = useState([])

  useEffect(() => {
    fetchSettings()
    fetchAngelOneSettings()
    fetchLogs()
    
    // Check for Kite callback
    const urlParams = new URLSearchParams(window.location.search)
    const requestToken = urlParams.get('request_token')
    if (requestToken) {
      handleKiteCallback(requestToken)
    }
  }, [])

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.getSettings()
      setSettings(prev => ({
        ...prev,
        ...response.data,
        kiteApiKey: response.data.kiteApiKey || ''
      }))
    } catch (error) {
      console.error('Settings fetch error:', error)
      toast.error('Failed to fetch settings')
    } finally {
      setLoading(false)
    }
  }

  const fetchAngelOneSettings = async () => {
    try {
      const res = await settingsAPI.getAngelOneSettings()
      setAoSettings(prev => ({ ...prev, ...res.data, angelOneMpin: '', angelOneTotpKey: '' }))
    } catch { /* ignore */ }
  }

  const saveAngelOneSettings = async () => {
    setAoSaving(true)
    try {
      await settingsAPI.updateAngelOneSettings({
        apiKey:     aoSettings.angelOneApiKey     || undefined,
        clientCode: aoSettings.angelOneClientCode || undefined,
        mpin:       aoSettings.angelOneMpin       || undefined,
        totpKey:    aoSettings.angelOneTotpKey    || undefined,
      })
      if (aoSettings.streamingProvider) {
        await settingsAPI.updateStreamingProvider(aoSettings.streamingProvider)
      }
      toast.success('Streaming settings saved')
    } catch { toast.error('Failed to save streaming settings') }
    finally { setAoSaving(false) }
  }

  const fetchLogs = async () => {
    try {
      const response = await settingsAPI.getLogs()
      setLogs(response.data)
    } catch (error) {
      console.error('Logs fetch error:', error)
    }
  }

  const handleKiteCallback = async (requestToken) => {
    try {
      await connectKite(requestToken)
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname)
    } catch (error) {
      console.error('Kite callback error:', error)
    }
  }

  const updateKiteApiKey = async () => {
    if (!settings.kiteApiKey.trim()) {
      toast.error('Please enter a valid API key')
      return
    }

    setSaving(true)
    try {
      await settingsAPI.updateKiteApiKey(settings.kiteApiKey)
      toast.success('Kite API key updated successfully')
    } catch (error) {
      console.error('Update API key error:', error)
      toast.error('Failed to update API key')
    } finally {
      setSaving(false)
    }
  }

  const connectToKite = async () => {
    try {
      const response = await authAPI.getKiteLoginUrl()
      window.location.href = response.data.loginUrl
    } catch (error) {
      console.error('Kite login URL error:', error)
      toast.error('Failed to get Kite login URL')
    }
  }

  const toggleAgentMode = async () => {
    setSaving(true)
    try {
      const newValue = !settings.agentMode
      await settingsAPI.toggleAgentMode(newValue)
      setSettings(prev => ({ ...prev, agentMode: newValue }))
      toast.success(`Agent mode ${newValue ? 'enabled' : 'disabled'}`)
    } catch (error) {
      console.error('Toggle agent mode error:', error)
      toast.error('Failed to toggle agent mode')
    } finally {
      setSaving(false)
    }
  }

  const updateNotifications = async (field, value) => {
    setSaving(true)
    try {
      const newNotifications = { ...settings.notifications, [field]: value }
      await settingsAPI.updateNotifications(newNotifications)
      setSettings(prev => ({ 
        ...prev, 
        notifications: newNotifications 
      }))
      toast.success('Notification settings updated')
    } catch (error) {
      console.error('Update notifications error:', error)
      toast.error('Failed to update notifications')
    } finally {
      setSaving(false)
    }
  }

  const updateTradingSettings = async () => {
    setSaving(true)
    try {
      await settingsAPI.updateTradingSettings(settings.trading)
      toast.success('Trading settings updated')
    } catch (error) {
      console.error('Update trading settings error:', error)
      toast.error('Failed to update trading settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* ── Streaming Configuration ── */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-5">
              <Radio className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">Streaming</h3>
            </div>

            {/* Provider toggle */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">Streaming Provider</label>
              <div className="flex gap-3">
                {['kite', 'angelone'].map(p => (
                  <button
                    key={p}
                    onClick={() => setAoSettings(prev => ({ ...prev, streamingProvider: p }))}
                    className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-semibold transition-colors ${
                      aoSettings.streamingProvider === p
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {p === 'kite' ? '🟠 Kite (Zerodha)' : '🔵 AngelOne'}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-gray-400">
                Kite uses your existing Zerodha session. AngelOne uses the SmartAPI WebSocket (faster, no Kite required).
              </p>
            </div>

            {/* AngelOne credentials */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">AngelOne SmartAPI Credentials</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">API Key</label>
                  <input
                    type="text"
                    value={aoSettings.angelOneApiKey}
                    onChange={e => setAoSettings(prev => ({ ...prev, angelOneApiKey: e.target.value }))}
                    placeholder="Your AngelOne API key"
                    className="input w-full text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Client Code</label>
                  <input
                    type="text"
                    value={aoSettings.angelOneClientCode}
                    onChange={e => setAoSettings(prev => ({ ...prev, angelOneClientCode: e.target.value }))}
                    placeholder="e.g. A123456"
                    className="input w-full text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">MPIN</label>
                  <input
                    type="password"
                    value={aoSettings.angelOneMpin}
                    onChange={e => setAoSettings(prev => ({ ...prev, angelOneMpin: e.target.value }))}
                    placeholder={aoSettings.angelOneMpinSet ? '••••  (saved — enter to change)' : '4–6 digit PIN'}
                    className="input w-full text-sm"
                    maxLength={6}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    TOTP Secret Key
                    <span className="ml-1 text-gray-400 font-normal">(base32, not the 6-digit code)</span>
                  </label>
                  <input
                    type="password"
                    value={aoSettings.angelOneTotpKey}
                    onChange={e => setAoSettings(prev => ({ ...prev, angelOneTotpKey: e.target.value }))}
                    placeholder={aoSettings.angelOneTotpKeySet ? '•••••  (saved — enter to change)' : 'JBSWY3DPEHPK3PXP…'}
                    className="input w-full text-sm font-mono"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Found in the AngelOne app under "Enable TOTP" — it's the secret you scan with an authenticator.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={saveAngelOneSettings}
                disabled={aoSaving}
                className="flex items-center gap-2 btn-primary text-sm"
              >
                {aoSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Streaming Settings
              </button>
            </div>
          </div>

          {/* Kite API Configuration */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <Key className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">Kite API Configuration</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                </label>
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={settings.kiteApiKey}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      kiteApiKey: e.target.value 
                    }))}
                    placeholder="Enter your Kite API key"
                    className="input flex-1"
                  />
                  <button
                    onClick={updateKiteApiKey}
                    disabled={saving}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {saving ? <LoadingSpinner size="small" /> : <Save className="w-4 h-4" />}
                    <span>Save</span>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    user?.kiteConnected ? 'bg-success-500' : 'bg-gray-400'
                  }`} />
                  <div>
                    <p className="font-medium text-gray-900">
                      {user?.kiteConnected ? 'Connected' : 'Not Connected'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {user?.kiteConnected 
                        ? `User ID: ${user.kiteUserId}` 
                        : 'Connect your Kite account to start trading'
                      }
                    </p>
                  </div>
                </div>
                {!user?.kiteConnected && settings.kiteApiKey && (
                  <button
                    onClick={connectToKite}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Connect to Kite</span>
                  </button>
                )}
                {user?.kiteConnected && (
                  <button
                    onClick={connectToKite}
                    className="btn-secondary flex items-center space-x-2 ml-2"
                    title="Refresh Kite Token"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Refresh Token</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Agent Mode */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <Bot className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">Agent Mode</h3>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Autonomous Trading</p>
                <p className="text-sm text-gray-600">
                  Enable AI agent to automatically execute trading signals
                </p>
              </div>
              <button
                onClick={toggleAgentMode}
                disabled={saving}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.agentMode ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.agentMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {settings.agentMode && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Agent Mode is Active
                    </p>
                    <p className="text-sm text-yellow-700">
                      The AI agent will automatically execute trading signals based on your configured parameters.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Trading Settings */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <Shield className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">Trading Parameters</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Risk Percentage per Trade
                </label>
                <input
                  type="number"
                  min="0.1"
                  max="10"
                  step="0.1"
                  value={settings.trading.riskPercentage}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    trading: { ...prev.trading, riskPercentage: parseFloat(e.target.value) }
                  }))}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Positions
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={settings.trading.maxPositions}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    trading: { ...prev.trading, maxPositions: parseInt(e.target.value) }
                  }))}
                  className="input"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Auto Execute Signals</p>
                  <p className="text-sm text-gray-600">
                    Automatically execute high-confidence signals
                  </p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    trading: { ...prev.trading, autoExecute: !prev.trading.autoExecute }
                  }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.trading.autoExecute ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.trading.autoExecute ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <button
                onClick={updateTradingSettings}
                disabled={saving}
                className="btn-primary flex items-center space-x-2"
              >
                {saving ? <LoadingSpinner size="small" /> : <Save className="w-4 h-4" />}
                <span>Save Trading Settings</span>
              </button>
            </div>
          </div>

          {/* Notifications */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <Bell className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Email Notifications</p>
                  <p className="text-sm text-gray-600">Receive trading alerts via email</p>
                </div>
                <button
                  onClick={() => updateNotifications('email', !settings.notifications.email)}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.notifications.email ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.notifications.email ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Web Notifications</p>
                  <p className="text-sm text-gray-600">Browser push notifications</p>
                </div>
                <button
                  onClick={() => updateNotifications('web', !settings.notifications.web)}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.notifications.web ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.notifications.web ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Telegram Notifications</p>
                  <p className="text-sm text-gray-600">Receive alerts on Telegram</p>
                </div>
                <button
                  onClick={() => updateNotifications('telegram', !settings.notifications.telegram)}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.notifications.telegram ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.notifications.telegram ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* System Status & Logs */}
        <div className="space-y-6">
          {/* System Status */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <Activity className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">System Status</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Kite Connection</span>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    user?.kiteConnected ? 'bg-success-500' : 'bg-gray-400'
                  }`} />
                  <span className="text-sm font-medium">
                    {user?.kiteConnected ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Agent Mode</span>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    settings.agentMode ? 'bg-primary-500 animate-pulse' : 'bg-gray-400'
                  }`} />
                  <span className="text-sm font-medium">
                    {settings.agentMode ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Database</span>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-success-500" />
                  <span className="text-sm font-medium">Connected</span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Logs */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">System Logs</h3>
              <button
                onClick={fetchLogs}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Refresh
              </button>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {logs.map((log, index) => (
                <div key={index} className="flex items-start space-x-3 p-2 bg-gray-50 rounded">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    log.level === 'SUCCESS' ? 'bg-success-500' :
                    log.level === 'WARNING' ? 'bg-yellow-500' :
                    log.level === 'ERROR' ? 'bg-danger-500' : 'bg-blue-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{log.message}</p>
                    <p className="text-xs text-gray-600">{log.details}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(log.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              
              {logs.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  <p className="text-sm">No recent logs</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Settings